import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Alert,
  Snackbar
} from '@mui/material';
import { setSelectedPatient, fetchPatientDetails } from '../../redux/slices/patientsSlice';
import { fetchFacilities } from '../../redux/thunks/facilitiesThunks';
import { fetchPatientPrescriptions } from '../../redux/slices/prescriptionsSlice';
import PatientBase from './PatientBase';
import format from 'date-fns/format';
import '../../styles/components.css';
import '../../styles/prescriptions.css';
import '../../styles/patientDetailsEnhancements.css';
import '../../styles/patientCardStyles.css';
import '../../styles/patientCardOutline.css';
import '../../styles/tabs.css';
import '../../styles/aiSummary.css';
import { isEmailUnique } from '../../utils/patientValidation';
import patientPortalService from '../../services/patientPortalService';
import { updatePatient } from '../../services/patientService';
import { getPatientEncounters } from '../../services/encounterService';
import { ProcedureService } from '../../services/procedureService';

import { useUserData } from '../../hooks/useUserData';
import { getAuthToken } from '../../utils/authUtils';
import { useAuth } from '../../context/AuthContext';

const PatientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedPatient, loading, error } = useSelector((state) => state.patients);
  const { role, defaultFacility, user } = useUserData();
  const { loading: authLoading } = useAuth();
  const prescriptions = useSelector((state) => state.prescriptions.prescriptions);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [encounters, setEncounters] = useState([]);
  const [encountersLoading, setEncountersLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });



  const validateEmailForUpdate = async (email) => {
    if (!email) return true;
    if (email === selectedPatient.email) return true;
    
    return await isEmailUnique(email, id);
  };

  // Handle save operation
  const handleSave = async () => {
    try {
      setSaving(true);
      setFormError(null);
      
      // Validate email if it's being updated
      if (formData.email) {
        const isEmailValid = await validateEmailForUpdate(formData.email);
        if (!isEmailValid) {
          setFormError('Email is already in use by another patient');
          return;
        }
      }

      // Update patient through service layer
      await updatePatient(id, formData);
      
      // Update Redux state
      dispatch(setSelectedPatient({
        ...selectedPatient,
        ...formData
      }));
      
      // PatientBase will handle closing edit mode internally
    } catch (error) {
      console.error('Error updating patient:', error);
      setFormError('Failed to update patient information');
    } finally {
      setSaving(false);
    }
  };

  // Handle patient portal toggle
  const handlePatientPortalToggle = async (patientId, isEnabled) => {
    try {
      setSaving(true);
      
      // Update patient portal status through service
      await patientPortalService.updatePatientPortalStatus(patientId, isEnabled, selectedPatient.firebaseUid);
      
      // Refresh patient data
      dispatch(fetchPatientDetails(patientId));
      
      setSnackbar({
        open: true,
        message: `Patient portal ${isEnabled ? 'enabled' : 'disabled'} successfully`,
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error updating patient portal status:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update patient portal status',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const fetchPatientEncounters = useCallback(async (patientId) => {
    try {
      setEncountersLoading(true);
     
      const result = await getPatientEncounters(patientId, { facilityId: defaultFacility?.id });
      const encountersList = result?.encounters || [];
      setEncounters(encountersList);
    } catch (error) {
      console.error('Error fetching encounters:', error);
    } finally {
      setEncountersLoading(false);
    }
  }, [defaultFacility]);

  const fetchPatientOrders = useCallback(async (patientId) => {
    try {
      setOrdersLoading(true);
   
      const token = await getAuthToken();
      
      // Initialize procedure service inside the callback to avoid dependency issues
      const procedureService = new ProcedureService();
      const ordersList = await procedureService.getPatientProcedures(patientId, token);
      setOrders(ordersList);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  // Initialize form data and fetch encounters and orders
  // Wait for authentication to be ready before fetching data
  useEffect(() => {
    // Don't fetch if auth is still loading or user is not authenticated
    if (authLoading || !user) {
      return;
    }
    
    if (id) {
      dispatch(fetchPatientDetails(id));
      dispatch(fetchPatientPrescriptions(id));
      fetchPatientEncounters(id);
      fetchPatientOrders(id);
    }
    // Use appropriate facility fetch based on user role
    if (role === 'admin') {
      dispatch(fetchFacilities());
    }
  }, [id, dispatch, role, fetchPatientEncounters, fetchPatientOrders, authLoading, user]);

  useEffect(() => {
    if (selectedPatient) {
      setFormData({
        firstName: selectedPatient.firstName || selectedPatient.name?.split(' ')[0] || '',
        lastName: selectedPatient.lastName || selectedPatient.name?.split(' ').slice(1).join(' ') || '',
        email: selectedPatient.email || '',
        contact: selectedPatient.contact || '',
        dateOfBirth: selectedPatient.dateOfBirth || '',
        gender: selectedPatient.gender || '',
        bloodType: selectedPatient.bloodType || '',
        address: selectedPatient.address || '',
        emergencyContact: selectedPatient.emergencyContact || '',
        status: selectedPatient.status || 'Active',
      });
    }
  }, [selectedPatient]);

  // Show loading while auth is initializing or data is loading
  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="text.secondary">
          {authLoading ? 'Initializing...' : 'Loading patient details...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (!selectedPatient) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="text.secondary">Patient not found</Typography>
      </Box>
    );
  }

  return (
    <>
      <PatientBase
        mode="view"
        patient={selectedPatient}
        encounters={encounters}
        prescriptions={prescriptions || []}
        orders={orders || []}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        onBack={() => navigate('/patients')}
        onEdit={() => {}} // PatientBase will handle edit mode internally
        onPatientPortalToggle={handlePatientPortalToggle}
        role={role}
        loading={loading || encountersLoading || ordersLoading}
        saving={saving}
        title={selectedPatient.name || 'Patient Details'}
        subtitle={`${selectedPatient.mrn ? (selectedPatient.mrn.startsWith('MRN') ? selectedPatient.mrn : `MRN: ${selectedPatient.mrn}`) : 'No MRN'} • ${selectedPatient.gender || 'Unknown'} • ${selectedPatient.dateOfBirth ? format(new Date(selectedPatient.dateOfBirth), 'MMM dd, yyyy') : 'DOB not specified'}`}
        facilityId={selectedPatient.facilityId}
      />

      {/* Error Display */}
      {formError && (
        <Snackbar
          open={!!formError}
          autoHideDuration={6000}
          onClose={() => setFormError(null)}
        >
          <Alert
            onClose={() => setFormError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {formError}
          </Alert>
        </Snackbar>
      )}

      {/* Success/Info Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PatientDetails;
